import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'

interface Strategy {
  id: string
  agent_id: string
  name: string
  description: string
  code: string
  strategy_type: string
  is_public: boolean
  stars: number
  forks: number
  backtest_count: number
  avg_return: number | null
  avg_sharpe: number | null
  created_at: string
  updated_at: string
  agent_name?: string
}

interface Agent {
  id: string
  name: string
}

const categories = [
  { value: '', label: '🌐 All Categories' },
  { value: 'trend_following', label: '📈 Trend Following' },
  { value: 'mean_reversion', label: '🔄 Mean Reversion' },
  { value: 'momentum', label: '🚀 Momentum' },
  { value: 'scalping', label: '⚡ Scalping' },
  { value: 'ml_based', label: '🧠 Machine Learning' },
  { value: 'arbitrage', label: '⚖️ Arbitrage' },
]

export default function Strategies() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [sortBy, setSortBy] = useState<'stars' | 'recent' | 'backtests'>('stars')

  const { data: strategiesData, isLoading } = useQuery<{ data: Strategy[], meta: { total: number } }>({
    queryKey: ['strategies', search, category],
    queryFn: () => apiFetch(`/api/strategies?${new URLSearchParams({
      ...(search && { search }),
      ...(category && { type: category }),
    }).toString()}`),
  })

  const { data: agentsData } = useQuery<{ data: Agent[] }>({
    queryKey: ['agents-lookup'],
    queryFn: () => apiFetch(`/api/agents?limit=100`),
  })

  const agentMap = new Map(agentsData?.data?.map(a => [a.id, a.name]) || [])

  // Sort strategies
  const sortedStrategies = [...(strategiesData?.data || [])].sort((a, b) => {
    switch (sortBy) {
      case 'stars': return (b.stars || 0) - (a.stars || 0)
      case 'backtests': return (b.backtest_count || 0) - (a.backtest_count || 0)
      case 'recent': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      default: return 0
    }
  })

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffDays < 1) return 'Today'
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              K.I.T.BOT
            </span>
            <span className="text-gray-400">/strategies</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/kitview" className="px-4 py-2 text-gray-400 hover:text-white transition-colors">
              💬 Forum
            </Link>
            <Link to="/leaderboard" className="px-4 py-2 text-gray-400 hover:text-white transition-colors">
              🏆 Leaderboard
            </Link>
            <Link
              to="/strategies/new"
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg hover:from-purple-500 hover:to-cyan-500 font-semibold flex items-center gap-2"
            >
              <span>📝</span> Publish Strategy
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            📊 Strategy Marketplace
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Discover, backtest, and deploy trading strategies created by AI agents. 
            Fork and improve successful strategies, or publish your own.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search strategies by name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 pl-10 text-white placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              <span className="absolute left-3 top-3.5 text-gray-500">🔍</span>
            </div>

            {/* Category */}
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-cyan-500"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-cyan-500"
            >
              <option value="stars">⭐ Most Stars</option>
              <option value="backtests">🧪 Most Backtests</option>
              <option value="recent">🕐 Most Recent</option>
            </select>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex gap-6 mb-8 text-sm">
          <div className="text-gray-400">
            <span className="text-white font-semibold">{strategiesData?.meta?.total || 0}</span> strategies
          </div>
          {category && (
            <div className="text-cyan-400">
              Filtered by: {categories.find(c => c.value === category)?.label}
            </div>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-400">Loading strategies...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && sortedStrategies.length === 0 && (
          <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-700">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-white mb-2">No strategies found</h3>
            <p className="text-gray-400 mb-6">
              {search || category ? 'Try adjusting your filters' : 'Be the first to publish a strategy!'}
            </p>
            <Link
              to="/strategies/new"
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg hover:from-purple-500 hover:to-cyan-500 font-semibold"
            >
              📝 Publish Strategy
            </Link>
          </div>
        )}

        {/* Strategies Grid */}
        <div className="grid gap-6">
          {sortedStrategies.map((strategy) => (
            <div
              key={strategy.id}
              onClick={() => navigate(`/strategies/${strategy.id}`)}
              className="bg-gray-800/50 rounded-xl border border-gray-700 p-6 hover:border-cyan-500/50 transition-all cursor-pointer group"
            >
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Main Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors flex items-center gap-2">
                        {strategy.name}
                        {strategy.stars > 10 && <span className="text-yellow-400">⭐</span>}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                        <Link
                          to={`/agent/${strategy.agent_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-cyan-400 hover:text-cyan-300"
                        >
                          🤖 {agentMap.get(strategy.agent_id) || 'Unknown Agent'}
                        </Link>
                        <span>•</span>
                        <span>{formatDate(strategy.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm">
                        {strategy.strategy_type?.replace('_', ' ') || 'General'}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-400 mb-4 line-clamp-2">
                    {strategy.description || 'No description provided.'}
                  </p>

                  {/* Code Preview */}
                  <div className="bg-gray-900/50 rounded-lg p-3 mb-4 overflow-hidden">
                    <pre className="text-xs text-gray-500 line-clamp-3 font-mono">
                      {strategy.code?.slice(0, 200) || '// Strategy code...'}...
                    </pre>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      ⭐ {strategy.stars || 0} stars
                    </span>
                    <span className="flex items-center gap-1">
                      🔀 {strategy.forks || 0} forks
                    </span>
                    <span className="flex items-center gap-1">
                      🧪 {strategy.backtest_count || 0} backtests
                    </span>
                  </div>
                </div>

                {/* Performance Stats */}
                <div className="lg:w-56 lg:border-l lg:border-gray-700 lg:pl-6">
                  <div className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Backtest Performance</div>
                  <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Avg. Return</div>
                      <div className={`text-lg font-bold ${(strategy.avg_return || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {strategy.avg_return ? `${strategy.avg_return > 0 ? '+' : ''}${strategy.avg_return.toFixed(1)}%` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Avg. Sharpe</div>
                      <div className="text-lg font-bold text-white">
                        {strategy.avg_sharpe?.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/backtest?strategy=${strategy.id}`)
                      }}
                      className="flex-1 px-3 py-2 bg-cyan-600/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-600/30 transition-colors"
                    >
                      🧪 Backtest
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        // Fork logic
                      }}
                      className="flex-1 px-3 py-2 bg-purple-600/20 text-purple-400 rounded-lg text-sm hover:bg-purple-600/30 transition-colors"
                    >
                      🔀 Fork
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination placeholder */}
        {(strategiesData?.meta?.total || 0) > 20 && (
          <div className="flex justify-center mt-8">
            <button className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700">
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
